import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Collapse, Container, IconButton, LinearProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import chevronDownFill from '@iconify/icons-eva/chevron-down-fill';
import chevronRightFill from '@iconify/icons-eva/chevron-right-fill';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import StatusChip from './shared/StatusChip';
import KpiCard from './shared/KpiCard';
import { getLiveMonitor, listEnrollments } from '../../utils/outreachApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const pct = (num, den) => (den ? `${Math.round((num / den) * 100)}%` : '—');

const DrillRow = ({ campaign }) => {
  const [open, setOpen] = useState(false);
  const [enrollments, setEnrollments] = useState(null);
  const navigate = useNavigate();

  const toggle = () => {
    setOpen(!open);
    if (!enrollments) {
      listEnrollments({ campaign_id: campaign.id }).then(({ data }) => setEnrollments(data));
    }
  };

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding="none">
          <IconButton size="small" onClick={toggle}>
            <Icon icon={open ? chevronDownFill : chevronRightFill} />
          </IconButton>
        </TableCell>
        <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/outreach/campaigns/${campaign.id}`)}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{campaign.name}</Typography>
          <Typography variant="caption" color="text.secondary">{campaign.description}</Typography>
        </TableCell>
         <TableCell><StatusChip status={campaign.status} /></TableCell>
         <TableCell align="right">{campaign.pm_assignments.length}</TableCell>
         <TableCell align="right">{campaign.counts.enrolled_count.toLocaleString()}</TableCell>
         <TableCell align="right">{campaign.counts.sent_count.toLocaleString()}</TableCell>
         <TableCell align="right">{campaign.counts.opened_count.toLocaleString()}</TableCell>
         <TableCell align="right">{campaign.counts.replied_count.toLocaleString()}</TableCell>
         <TableCell align="right">{pct(campaign.counts.replied_count, campaign.counts.delivered_count)}</TableCell>
         <TableCell align="right">{campaign.counts.failed_count + campaign.counts.bounced_count}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ pb: 0, pt: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">
                Enrollments — {campaign.name}
              </Typography>
              {!enrollments ? <LinearProgress sx={{ mt: 1 }} /> : enrollments.length === 0 ? (
                <Typography variant="body2" sx={{ mt: 1 }}>No enrollment rows yet.</Typography>
              ) : (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>BA</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reply at</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrollments.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.company_name}</TableCell>
                        <TableCell>{e.assigned_pm.name}</TableCell>
                        <TableCell>Step {e.current_step_order}</TableCell>
                        <TableCell><StatusChip status={e.status} /></TableCell>
                        <TableCell>{fmt(e.reply_received_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LiveMonitor = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => getLiveMonitor().then(({ data }) => setData(data));
    load();
    const t = setInterval(load, 15000); // simulate live updates
    return () => clearInterval(t);
  }, []);

  return (
    <Page title="Live Monitor | CRM">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader title="Live Monitor" subtitle="Real-time status across every active campaign" />
        {!data ? <LinearProgress /> : (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <KpiCard label="Active campaigns" value={data.summary.active_campaigns} color="success.main" />
              <KpiCard label="In flight" value={data.summary.in_flight.toLocaleString()}
                       hint="enrolled, awaiting reply" />
              <KpiCard label="Replied total" value={data.summary.replied_total.toLocaleString()}
                       color="warning.dark" />
              <KpiCard label="Errors" value={data.summary.error_total} color="error.main" />
            </Stack>
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Campaign</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">BAs</TableCell>
                    <TableCell align="right">Enrolled</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Opens</TableCell>
                    <TableCell align="right">Replies</TableCell>
                    <TableCell align="right">Reply %</TableCell>
                    <TableCell align="right">Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.campaigns.map((c) => <DrillRow key={c.id} campaign={c} />)}
                </TableBody>
              </Table>
            </Card>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Auto-refreshes every 15s · webhooks &lt;5min latency · Outlook poll every 5min
            </Typography>
          </>
        )}
      </Container>
    </Page>
  );
};

export default LiveMonitor;
